import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import * as Yup from "yup";

import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import { ATBuyerSellerDetails } from "../../atc-models/atc-org";
import useNotification from "../../../../shared/hooks/useNotification";
import { buyerSellerDetailsService } from "../../../v4/transaction-certificates/tc-service/buyer-seller-details.service";
import { useRecentTEIds } from "../../../../shared/hooks/useRecentTEIds";
import { atcService } from "../../atc-service/atc.service";

// ------------------ Props ------------------
interface BuyerSellerDetailsFormProps {
  formRef: React.RefObject<any>;
  detailsData: ATBuyerSellerDetails;
  setDetailsData: (data: ATBuyerSellerDetails) => void;
}

// ------------------ Component ------------------
const BuyerSellerDetailsForm: React.FC<BuyerSellerDetailsFormProps> = ({
  formRef,
  detailsData,
  setDetailsData,
}) => {
  const { notify } = useNotification();
  const { recentTEIds, saveRecentlySelectedTEId } = useRecentTEIds();

  // ------------------ State ------------------
  const [visible, setVisible] = useState({
    buyerName: true,
    buyerTeIdLabel: true,
  });

  const [disable, setDisable] = useState({
    sellerAddress: true,
    sellerState: true,
    sellerCountry: true,
    sellerPostalCode: true,
    buyerAddress: true,
    buyerState: true,
    buyerCountry: true,
    buyerPostalCode: true,
    buyerScNumber: true,
  });

  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);
  const [sellerStates, setSellerStates] = useState<{ value: string; label: string }[]>([]);
  const [buyerStates, setBuyerStates] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result: any = await atcService.getCountry();
        const countryOptions = result.data.map((t: any) => ({
          value: t.code_1,
          label: t.description_1 + " (" + t.code_1 + ")",
        }));
        setCountries(countryOptions);
      } catch (err) {
        console.error("Error fetching countries:", err);
      }
    };
    fetchCountries();
  }, []);

  const getStatesForCountry = async (countryCode: string, isBuyer: boolean) => {
    try {
      const formattedCode = JSON.stringify(
        `[${[countryCode].map((item) => `'${item}'`)}]`
      ).replace(/^"|"$/g, "");
      const result: any = await atcService.getStateBasedonCountry(formattedCode);
      const stateOptions = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      if (isBuyer) {
        setBuyerStates(stateOptions);
      } else {
        setSellerStates(stateOptions);
      }
    } catch (err) {
      console.error("Error fetching states:", err);
    }
  };

  // ------------------ Validation ------------------
const validationSchema = Yup.object().shape({
  sellerTeId: Yup.string(), // may be text or search based on flag
  sellerAddress: Yup.string().required("Seller Address is required"),
  sellerName: Yup.string().required("Seller Name is required"),
  sellerState: Yup.string().required("Seller State is required"),
  sellerCountry: Yup.string().required("Seller Country is required"), 
  sellerPostalCode: Yup.string(), 
  buyerTeId: Yup.string().required("Buyer TE ID is required"),
  buyerAddress: Yup.string().required("Buyer Address is required"),
  buyerName: Yup.string().required("Buyer Name is required"),
  buyerScNumber: Yup.string().required("Buyer SC Number is required"),
  buyerState: Yup.string().required("Buyer State is required"), 
  buyerCountry: Yup.string().required("Buyer Country is required"), 
  buyerPostalCode: Yup.string(), 
});

  // ------------------ API ------------------
  const fetchTEDetailsFromAPI = async (
    value: string,
    offset: number,
    type: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Failed to fetch TE details", "error");
      return [];
    }
  };

  // ------------------ Seller Handlers ------------------
  const clearSellerLookupFields = (includeName = true) => {
    const fields = [
      "sellerTeId",
      "sellerAddress",
      "sellerState",
      "sellerCountry",
      "sellerPostalCode",
    ];

    if (includeName) {
      fields.splice(1, 0, "sellerName");
    }

    fields.forEach((field) => formRef.current?.setFieldValue(field, ""));
    setDisable((prev) => ({
      ...prev,
      sellerAddress: false,
      sellerState: false,
      sellerCountry: false,
      sellerPostalCode: false,
    }));
  };

  const handleSellerTEIdValueChange = (value: any) => {
    if (value == null) {
      clearSellerLookupFields();
    } else {
      // Populate
      // Save selected TE details to recent list
      saveRecentlySelectedTEId(value);
      formRef.current?.setFieldValue("sellerTeId", value.TE_ID);
      formRef.current?.setFieldValue("sellerName", value.account_name);
      formRef.current?.setFieldValue(
        "sellerAddress",
        value.street || ""
      );
      formRef.current?.setFieldValue(
        "sellerState",
        value.state || ""
      );
      formRef.current?.setFieldValue(
        "sellerCountry",
        value.country || ""
      );
      formRef.current?.setFieldValue(
        "sellerPostalCode",
        value.postal_code || ""
      );

      if (value.country) {
        getStatesForCountry(value.country, false);
      }

      setDisable((prev) => ({
        ...prev,
        sellerAddress: Boolean(value.street),
        sellerState: Boolean(value.state),
        sellerCountry: Boolean(value.country),
        sellerPostalCode: Boolean(value.postal_code),
      }));
    }
  };

  const handleSellerNamedValueChange = (value: any) => {
    if (value == null) {
      clearSellerLookupFields();
    } else {
      formRef.current?.setFieldValue("sellerName", value.account_name);
      formRef.current?.setFieldValue("sellerTeId", value.TE_ID);

      // Address
      if (value.street) {
        formRef.current?.setFieldValue("sellerAddress", value.street);
        setDisable((prev) => ({ ...prev, sellerAddress: true }));
      } else {
        formRef.current?.setFieldValue("sellerAddress", "");
        setDisable((prev) => ({ ...prev, sellerAddress: false }));
      }

      // Country
      if (value.country) {
        formRef.current?.setFieldValue("sellerCountry", value.country);
        setDisable((prev) => ({ ...prev, sellerCountry: true }));
        getStatesForCountry(value.country, false);
      } else {
        formRef.current?.setFieldValue("sellerCountry", "");
        setDisable((prev) => ({ ...prev, sellerCountry: false }));
      }

      // State
      if (value.state) {
        formRef.current?.setFieldValue("sellerState", value.state);
        setDisable((prev) => ({ ...prev, sellerState: true }));
      } else {
        formRef.current?.setFieldValue("sellerState", "");
        setDisable((prev) => ({ ...prev, sellerState: false }));
      }

      // Postal Code
      if (value.postal_code) {
        formRef.current?.setFieldValue("sellerPostalCode", value.postal_code);
        setDisable((prev) => ({ ...prev, sellerPostalCode: true }));
      } else {
        formRef.current?.setFieldValue("sellerPostalCode", "");
        setDisable((prev) => ({ ...prev, sellerPostalCode: false }));
      }
    }
  };

  const handleSellerNameInputValueChange = (inputValue: string) => {
    if (inputValue === (formRef.current?.values?.sellerName ?? "")) {
      return;
    }

    formRef.current?.setFieldValue("sellerName", inputValue);
    clearSellerLookupFields(false);
  };

  // ------------------ Buyer Handlers ------------------
  const handleBuyerTEIdValueChange = (value: any) => {
    if (value == null) {
      ["buyerTeId", "buyerName", "buyerAddress", "buyerScNumber", "buyerState", "buyerCountry", "buyerPostalCode"].forEach(
        (field) => formRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerName: false,
        buyerAddress: false,
        buyerScNumber: false,
        buyerState: false,
        buyerCountry: false,
        buyerPostalCode: false,
      }));
      setVisible((prev) => ({
        ...prev,
        buyerName: true,
        buyerTeIdLabel: true,
      }));
    } else {
      // Save selected TE details to recent list
      saveRecentlySelectedTEId(value);
      formRef.current?.setFieldValue("buyerTeId", value.TE_ID);
      formRef.current?.setFieldValue("buyerName", value.account_name);
      formRef.current?.setFieldValue(
        "buyerAddress",
        value.street || ""
      );
      formRef.current?.setFieldValue(
        "buyerScNumber",
        value.sc_number || ""
      );
      formRef.current?.setFieldValue(
        "buyerCountry",
        value.country || ""
      );
      formRef.current?.setFieldValue(
        "buyerState",
        value.state || ""
      );
      formRef.current?.setFieldValue(
        "buyerPostalCode",
        value.postal_code || ""
      );

      if (value.country) {
        getStatesForCountry(value.country, true);
      }

      setDisable((prev) => ({
        ...prev,
        buyerName: Boolean(value.account_name),
        buyerAddress: Boolean(value.street),
        buyerScNumber: false, // always editable
        buyerState: Boolean(value.state),
        buyerCountry: Boolean(value.country),
        buyerPostalCode: Boolean(value.postal_code),
      }));

      setVisible((prev) => ({ ...prev, buyerName: false }));
    }
  };

  const handleBuyerNameValueChange = (value: any) => {
    if (value == null) {
      ["buyerAddress", "buyerName", "buyerTeId", "buyerScNumber", "buyerState", "buyerCountry", "buyerPostalCode"].forEach(
        (field) => formRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerAddress: false,
        buyerScNumber: false,
        buyerState: false,
        buyerCountry: false,
        buyerPostalCode: false,
      }));
      setVisible((prev) => ({ ...prev, buyerTeIdLabel: true }));
    } else {
      formRef.current?.setFieldValue("buyerName", value.account_name);
      formRef.current?.setFieldValue("buyerTeId", value.TE_ID);

      // Address
      if (value.street) {
        formRef.current?.setFieldValue("buyerAddress", value.street);
        setDisable((prev) => ({ ...prev, buyerAddress: true }));
      } else {
        formRef.current?.setFieldValue("buyerAddress", "");
        setDisable((prev) => ({ ...prev, buyerAddress: false }));
      }

      // SC Number
      if (value.sc_number) {
        formRef.current?.setFieldValue(
          "buyerScNumber",
          value.sc_number
        );
        setDisable((prev) => ({ ...prev, buyerScNumber: true }));
      } else {
        formRef.current?.setFieldValue("buyerScNumber", "");
        setDisable((prev) => ({ ...prev, buyerScNumber: false }));
      }

      // Country
      if (value.country) {
        formRef.current?.setFieldValue("buyerCountry", value.country);
        setDisable((prev) => ({ ...prev, buyerCountry: true }));
        getStatesForCountry(value.country, true);
      } else {
        formRef.current?.setFieldValue("buyerCountry", "");
        setDisable((prev) => ({ ...prev, buyerCountry: false }));
      }

      // State
      if (value.state) {
        formRef.current?.setFieldValue("buyerState", value.state);
        setDisable((prev) => ({ ...prev, buyerState: true }));
      } else {
        formRef.current?.setFieldValue("buyerState", "");
        setDisable((prev) => ({ ...prev, buyerState: false }));
      }

      // Postal Code
      if (value.postal_code) {
        formRef.current?.setFieldValue("buyerPostalCode", value.postal_code);
        setDisable((prev) => ({ ...prev, buyerPostalCode: true }));
      } else {
        formRef.current?.setFieldValue("buyerPostalCode", "");
        setDisable((prev) => ({ ...prev, buyerPostalCode: false }));
      }

      setVisible((prev) => ({ ...prev, buyerTeIdLabel: false }));
    }
  };

  // ------------------ Form Config ------------------
  const formConfig = {
    pageName: "buyer-seller-details",
    initialValues: { ...detailsData },
    validationSchema,
    enableReinitialize: true,
    sections: [
      {
        title: "Seller Details",
        fields: [
          {
            label: "Seller TE-ID",
            name: "sellerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: detailsData.sellerTeId,
            props: {
              placeholder: "Search Seller TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              param: "SellerTEId",
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              // Show recently selected TE IDs when the field is focused and empty
              fetchOnEmptyFocus: true,
              onSearch: (query: string, offset: number) =>
                !query || query.trim() === ""
                  ? Promise.resolve(
                      recentTEIds.length > 0 ? recentTEIds : []
                    )
                  : fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          {
            label: "Seller Name",
            name: "sellerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerName,
            props: {
              placeholder: "Search Seller Name",
              variant: "outlined",
              allowCustomValue: true,
              onChange: handleSellerNamedValueChange,
              onInputValueChange: handleSellerNameInputValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          // Seller Address (read-only when Yes + lookup, editable when No)
          {
            label: "Seller Address",
            name: "sellerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerAddress,
            // 🔑 key change: address is only disabled in the “Yes + auto-fill” flow
            disabled: disable.sellerAddress,
            props: {
              placeholder: "Enter Seller Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller Country
          {
            label: "Seller Country",
            name: "sellerCountry",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerCountry,
            disabled: disable.sellerCountry,
            events: {
              onChange: (newValue: any) => {
                getStatesForCountry(newValue, false);
                formRef.current?.setFieldValue("sellerState", "");
              },
            },
            props: {
              enableSearch: true,
              options: countries,
              placeholder: "Select Seller Country",
              fullWidth: true,
            },
          },
          // Seller State
          {
            label: "Seller State",
            name: "sellerState",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerState,
            disabled: disable.sellerState,
            props: {
              enableSearch: true,
              options: sellerStates,
              placeholder: "Select Seller State",
              fullWidth: true,
            },
          },
          // Seller Postal Code
          {
            label: "Seller Postal Code",
            name: "sellerPostalCode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: detailsData.sellerPostalCode,
            disabled: disable.sellerPostalCode,
            props: {
              placeholder: "Enter Seller Postal Code",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
      {
        title: "Buyer Details",
        fields: [
          // Buyer TE ID (Search with clear)
          {
            label: "Buyer TE-ID",
            name: "buyerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerTeId,
            visible: () => true,
            props: {
              placeholder: "Search Buyer TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              onChange: handleBuyerTEIdValueChange,
              displayKey: "TE_ID",
              // Show recently selected TE IDs when the field is focused and empty
              fetchOnEmptyFocus: true,
              onSearch: (query: string, offset: number) =>
                !query || query.trim() === ""
                  ? Promise.resolve(
                      recentTEIds.length > 0 ? recentTEIds : []
                    )
                  : fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          // Buyer Name (Search / Text)
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerName,
            visible: () => visible.buyerName,
            props: {
              placeholder: "Search Buyer Name",
              variant: "outlined",
              onChange: handleBuyerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: true,
            visible: () => !visible.buyerName,
            props: {
              placeholder: "Enter Buyer Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Address + SC Number
          {
            label: "Buyer Address",
            name: "buyerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerAddress,
            disabled: disable.buyerAddress,
            props: {
              placeholder: "Enter Buyer Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Buyer SC Number",
            name: "buyerScNumber",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerScNumber,
            disabled: false,
            props: {
              placeholder: "Enter SC Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Buyer Country
          {
            label: "Buyer Country",
            name: "buyerCountry",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerCountry,
            disabled: disable.buyerCountry,
            events: {
              onChange: (newValue: any) => {
                getStatesForCountry(newValue, true);
                formRef.current?.setFieldValue("buyerState", "");
              },
            },
            props: {
              enableSearch: true,
              options: countries,
              placeholder: "Select Buyer Country",
              fullWidth: true,
            },
          },
          // Buyer State
          {
            label: "Buyer State",
            name: "buyerState",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerState,
            disabled: disable.buyerState,
            props: {
              enableSearch: true,
              options: buyerStates,
              placeholder: "Select Buyer State",
              fullWidth: true,
            },
          },
          // Buyer Postal Code
          {
            label: "Buyer Postal Code",
            name: "buyerPostalCode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: detailsData.buyerPostalCode,
            disabled: disable.buyerPostalCode,
            props: {
              placeholder: "Enter Buyer Postal Code",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {setDetailsData(values);
      
      const errors = (await formRef.current?.validateForm()) ?? {};
      if (Object.keys(errors || {}).length > 0) {
        notify("Please fill all the required fields", "error");
        return;
      }
    },
  };

  // ------------------ Render ------------------
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 6, fontWeight: "bold" }}>
        Buyer & Seller Details
      </Typography>

      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </Box>
  );
};

export default BuyerSellerDetailsForm;
